import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import BEMHelper from '@ynap/bem-helper';
import { normalizeStringHelper } from '@ynap/normalize-string-helper';

const bem = new BEMHelper('CombinedSelect');

const Search = ({ handleItemKeyDown, onSearch, placeholder, value }) => (
    <div className={bem('search')}>
        <div className={bem('icon')} />
        <input
            autoFocus
            className={bem('input')}
            onChange={(e) => onSearch(e.target.value)}
            onClick={(e) => e.stopPropagation()}
            onKeyDown={(e) => handleItemKeyDown(e)}
            placeholder={placeholder}
            tabIndex="0"
            type="text"
            value={value}
        />
    </div>
);

class CombinedSelect extends PureComponent {
    listElementHeight = 40;

    constructor(props) {
        super(props);
        this.state = { open: false, messageCanTriggerDropdown: true, searchValue: '' };
        this.closeSelect = this.closeSelect.bind(this);
        this.toggle = this.toggle.bind(this);
        this.handleItemKeyDown = this.handleItemKeyDown.bind(this);
        this.handleTogglerKeyDown = this.handleTogglerKeyDown.bind(this);
    }

    componentDidUpdate(prevProps, prevState) {
        if (this.props.noSizeNotification && !this.state.open && this.state.messageCanTriggerDropdown) {
            this.toggle();
        }

        // Focus first element in the dropdown for screen readers
        if (this.justOpened && !this.props.enableSearch) {
            this.list?.firstElementChild.focus();
            this.justOpened = false;
        }

        if (prevProps.value !== this.props.value) {
            this.updatesSelectScroll();
        }

        if (this.props.onClose && prevState.open && !this.state.open) this.props.onClose();
    }

    componentDidMount() {
        document.addEventListener('mousedown', this.closeSelect);
        this.updatesSelectScroll();

        if (!this.props.numOfListElementsDisplayed) {
            return;
        }

        this.listElementHeight = this.listLabelElement ? this.listLabelElement.offsetHeight : this.listElementHeight;
        const listDiff = this.listElementHeight - this.list.clientHeight;
        this.setState({ openedHeight: `${this.props.numOfListElementsDisplayed * this.listElementHeight + listDiff}px` });
        this.setState({ open: !!this.props.openOnMount });
        this.props.openOnMount && this.mobileSelect.click();
    }

    updatesSelectScroll() {
        const ul = this.list;
        const ulArray = Array.from(ul?.children || []);
        const li =
            ul?.children?.length > 0 &&
            ulArray.find((elem) => {
                const dataValue = elem.attributes['data-value'];
                return dataValue && dataValue.value === this.props.value;
            });

        if (li) {
            ul.scrollTop = li.offsetTop;
        }
    }

    componentWillUnmount() {
        document.removeEventListener('mousedown', this.closeSelect);
    }

    closeSelect(e) {
        const { noSizeNotification, setNoSizeNotification } = this.props;
        this.setState({ messageCanTriggerDropdown: true });

        if (this.state.open && !this.node.contains(e.target)) {
            this.setState({ open: false, messageCanTriggerDropdown: false });
            this.updatesSelectScroll();
        }

        if (noSizeNotification) {
            setNoSizeNotification(null);
        }
    }

    toggle() {
        const { onDropdown = () => {}, tracking, trackOpenListSort } = this.props;

        if (!this.state.open) {
            tracking?.trackEvent(trackOpenListSort?.());
            onDropdown();
            // Mark first open action to trigger focus on first list item once rendered
            this.justOpened = true;
        } else {
            this.listLabelElement.focus();
        }

        this.setState({ messageCanTriggerDropdown: false, searchValue: '' });
        this.setState((prevState) => ({ open: !prevState.open }));
        this.updatesSelectScroll();
    }

    handleTogglerKeyDown(event) {
        const openDropdownKeys = [0, 32, 40, 38]; // Space, arrowUp, arrowDown keys
        const closeDropdownKeys = [0, 32, 27]; // Space, Escape keys
        const openDropdown = !this.state.open && openDropdownKeys.includes(event.keyCode);
        const closeDropdown = this.state.open && closeDropdownKeys.includes(event.keyCode);

        if (openDropdown || closeDropdown) {
            event.preventDefault();
            this.toggle();
        }
    }

    handleItemKeyDown(event, value = null) {
        const selectOptionKeys = [0, 32, 13]; // Space & Enter keys
        const closeDropdownKey = 27; // Escape key
        const arrowDownKey = 40;
        const arrowUpKey = 38;
        const { keyCode, target } = event;

        if (keyCode == arrowDownKey) {
            event.preventDefault();
            target.nextElementSibling ? target.nextElementSibling.focus() : this.list?.firstElementChild?.focus();
        } else if (keyCode == arrowUpKey) {
            event.preventDefault();
            target.previousElementSibling ? target.previousElementSibling.focus() : this.list?.lastElementChild?.focus();
        } else if (value && selectOptionKeys.includes(keyCode)) {
            event.preventDefault();
            this.selectOption(value);
            this.toggle();
        } else if (keyCode == closeDropdownKey) {
            this.toggle();
        }
    }

    buildOptions(options = [], currentValue, isOpened) {
        const customOptions = [];
        const nativeOptions = [];
        let selectedLabel = currentValue;

        const filteredOptions = this.props.searchFilter(this.state.searchValue, options);

        ((filteredOptions.length && filteredOptions) || options).forEach((opt) => {
            const { key, label, labelNative, value } = opt;
            let modifier;

            if (value === currentValue && isOpened) {
                modifier = 'selected';
            } else if (isOpened) {
                modifier = 'opened';
            } else {
                modifier = undefined;
            }

            if (value === currentValue) {
                selectedLabel = label;
            }

            customOptions.push(
                <li
                    key={key || value}
                    className={bem('option', modifier)}
                    data-value={value}
                    onClick={() => this.selectOption(value)}
                    onKeyDown={(e) => this.handleItemKeyDown(e, value)}
                    role="option"
                    tabIndex="0"
                >
                    {label}
                </li>,
            );

            nativeOptions.push(
                <option key={key || value} className={bem('option', modifier)} value={value}>
                    {labelNative || label}
                </option>,
            );
        });

        return {
            nativeOptions,
            customOptions,
            selectedLabel,
        };
    }

    selectOption(value) {
        if (this.props.value !== value) {
            this.props.onChange(value);
        }
    }

    render() {
        const {
            alternateBorder,
            ariaLabel,
            className,
            disabled,
            emptyLabel,
            enableSearch,
            hasError,
            noSizeNotification,
            numOfListElementsDisplayed,
            onChange,
            onDropdown,
            openOnMount, // eslint-disable-line no-unused-vars
            options, // eslint-disable-line no-unused-vars
            promptError, // eslint-disable-line no-unused-vars
            searchFilter, // eslint-disable-line no-unused-vars
            searchPlaceholder, // eslint-disable-line no-unused-vars
            selectRef, // eslint-disable-line no-unused-vars
            setNoSizeNotification,
            type,
            value,
            ...rest
        } = this.props;
        const { openedHeight } = this.state;
        // we have placeholder text and nothing has been selected yet, we are on the placeholder text.
        const placeholderText = emptyLabel && value !== undefined ? 'placeholderText' : 'selectableOption';
        const disabledSelect = disabled ? 'disabled' : '';
        const errorClass = hasError ? 'selectWithError' : undefined;
        const isOpened = this.state.open ? 'opened' : undefined;
        const isPromptError = promptError ? 'selectWithError' : undefined;
        const noLabelModifier = emptyLabel ? '' : 'noLabel';
        const alternateBorderClass = alternateBorder ? 'alternateBorder' : '';
        const { customOptions, nativeOptions, selectedLabel } = this.buildOptions(options, value, this.state.open);
        const defaultLabel = this.state.open ? emptyLabel : selectedLabel || emptyLabel;

        return (
            <div
                className={bem(null, [isOpened, type, disabledSelect], className)}
                ref={(node) => {
                    this.node = node;
                }}
            >
                <div className={bem('customizedSelect', isPromptError)} onClick={this.toggle} ref={selectRef}>
                    {emptyLabel && (
                        <div
                            aria-controls={this.list}
                            aria-expanded={this.state.open}
                            aria-label={ariaLabel || defaultLabel}
                            className={bem('label', [isOpened, errorClass, isPromptError, alternateBorderClass])}
                            onKeyDown={(e) => this.handleTogglerKeyDown(e)}
                            role="combobox"
                            tabIndex="0"
                            ref={(node) => {
                                this.listLabelElement = node;
                            }}
                        >
                            {defaultLabel}
                        </div>
                    )}
                    {isOpened && enableSearch && (
                        <Search
                            handleItemKeyDown={this.handleItemKeyDown}
                            onSearch={(searchValue) => this.setState({ searchValue })}
                            placeholder={searchPlaceholder || ''}
                            value={this.state.searchValue}
                        />
                    )}
                    <ul
                        aria-label="select-options"
                        className={bem('field', [type, isOpened, noLabelModifier, 'custom', isPromptError, errorClass])}
                        role="listbox"
                        style={isOpened && { maxHeight: openedHeight }}
                        ref={(node) => {
                            this.list = node;
                        }}
                    >
                        {customOptions}
                    </ul>
                </div>

                {/* Mobile and Tablet only using display:block; */}
                <select
                    className={bem('field', [type, placeholderText, 'nativeSelect', isPromptError, errorClass])}
                    onChange={(event) => onChange(event.target.value)}
                    value={value || 'emptyLabel'}
                    ref={(node) => {
                        typeof selectRef === 'function' && selectRef(node);
                        this.mobileSelect = node;
                    }}
                    {...rest}
                >
                    {emptyLabel && (
                        <option disabled value="emptyLabel">
                            {emptyLabel}
                        </option>
                    )}

                    {nativeOptions}
                </select>
            </div>
        );
    }
}

export const defaultSearchFilter = (searchValue, listData) => {
    /*
    Search should perform 'starts with' check (not 'contains'), e.g. 'str' should not return 'Austria'.
    For multi-word values search should check all words, e.g. 'sta' should return 'United States'.
    Search should be case-insensitive, e.g. 'uNi' should return 'United Kingdom'.
    Search should ignore leading and trailing whitespaces in the search term, e.g. '(space)uni(space)' should still return 'United Kingdom'.
    */
    const searchValueLower = searchValue ? normalizeStringHelper(searchValue.toLowerCase().trim()) : normalizeStringHelper(searchValue);
    return searchValueLower
        ? listData.filter(({ label, value }) => {
              const valueLower = normalizeStringHelper(value.toLowerCase());
              const valueLowerWords = valueLower.split(' ');

              const labelLower = normalizeStringHelper(label.toLowerCase());
              const labelLowerWords = labelLower.split(' ');

              return (
                  valueLower.startsWith(searchValueLower) ||
                  valueLowerWords.some((valueLowerWord) => valueLowerWord.startsWith(searchValueLower)) ||
                  labelLower.startsWith(searchValueLower) ||
                  labelLowerWords.some((labelWord) => normalizeStringHelper(labelWord).startsWith(searchValueLower))
              );
          })
        : [];
};

CombinedSelect.defaultProps = {
    numOfListElementsDisplayed: 10,
    setNoSizeNotification: () => {},
    enableSearch: false,
    searchFilter: defaultSearchFilter,
};

CombinedSelect.propTypes = {
    emptyLabel: PropTypes.string,
    className: PropTypes.string,
    alternateBorder: PropTypes.bool,
    children: PropTypes.node,
    selectRef: PropTypes.func,
    type: PropTypes.string,
    hasError: PropTypes.bool,
    value: PropTypes.string,
    onChange: PropTypes.func.isRequired,
    options: PropTypes.arrayOf(
        PropTypes.shape({
            value: PropTypes.string,
            key: PropTypes.string,
            label: PropTypes.string || PropTypes.node,
            labelNative: PropTypes.string,
        }),
    ),
    numOfListElementsDisplayed: PropTypes.number,
    onDropdown: PropTypes.func,
    onClose: PropTypes.func,
    promptError: PropTypes.bool,
    disabled: PropTypes.bool,
    openOnMount: PropTypes.bool,
    setNoSizeNotification: PropTypes.func,
    noSizeNotification: PropTypes.string,
    enableSearch: PropTypes.bool,
    searchFilter: PropTypes.func,
    tracking: PropTypes.shape({
        trackEvent: PropTypes.func,
    }),
};

export default CombinedSelect;
